import moment from 'moment';
export function hasNumber(myString: string) {
  return /\d/.test(myString);
}

export function capitalize(s: string) {
  const firstLetter = s?.charAt(0);
  return hasNumber(firstLetter) ? s : firstLetter?.toUpperCase() + s?.slice(1);
}

export function normalizeNamePart(s: string) {
  const firstLetter = s.charAt(0);
  const rest = s.slice(1)
  return firstLetter.toUpperCase() + rest.toLowerCase();
}

export function normalizeFullName(s: string) {
  let str = "";
  for (const part of s.split(" ")) {
    str += normalizeNamePart(part) + " "
  }
  return str.trim();
}

export function normalizeCompanyName(s: string) {
  const trimmed = s.trim();
  const companyTypeMatch = trimmed.match(/\b(a\/s|aps)\b/i);
  const companyType = companyTypeMatch
    ? companyTypeMatch[1].toLowerCase() === "a/s"
      ? "A/S"
      : "ApS"
    : "";
  const name = trimmed.replace(/\b(a\/s|aps)\b/i, "").trim();
  return companyType
    ? `${normalizeFullName(name)} ${companyType}`
    : normalizeFullName(name);
}

export const getRandomString = (length: number = 5) => (Math.random() + 1).toString(36).substring(length);

export const isValidVin = (vin: unknown): vin is string => {
  if (typeof vin === 'string' && vin.length === 17) {
    const vinPattern = /^[a-zA-Z0-9]+$/
    return vinPattern.test(vin)
  }
  return false;
}

export const formatVin = (vin: unknown) => {
  return String(vin).toUpperCase()
}

export const slugify = (text: string): string => {
  let str = text;
  str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str.replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens
  return str.length > 3 ? str : getRandomString(8);
}

export function printReadableECode(eCode: string) {
  let str = ''
  if (eCode.length >= 2) {
    str += eCode.slice(0, 2)
  }
  if (eCode.length >= 5) {
    str += '-' + eCode.slice(2, 5)
  }
  if (eCode.length >= 8) {
    str += '-' + eCode.slice(5, 8)
  }
  if (eCode.length >= 11) {
    str += '-' + eCode.slice(8, 11)
  }
  if (eCode.length >= 15) {
    str += '-' + eCode.slice(11, 15)
  }
  if (eCode.length >= 24) {
    str += '-' + eCode.slice(15, 24)
  }
  return str
}

const CAR_API_URL = process.env.REACT_APP_MOTOR_ATLAS_URL
export function constructMotorAtlasLink(
  motorAtlasId: string,
  motorAtlasTab?: "teknisk" | "nypris" | "udstyr",
  firstRegDate?: string | null,
  isVan?: boolean,
) {
  const s = new URLSearchParams()
  s.set("ecode", motorAtlasId)
  s.set("active-tab", motorAtlasTab ?? "teknisk")
  s.set("erhvervskoeretoej", isVan ? "true" : "false")
  if (firstRegDate) {
    s.set("year", moment(firstRegDate).year().toString())
  }
  const query = s.toString()
  return `${CAR_API_URL}/biler?${query}`
}


export function normalize(s?: string | null) {
  return (s ?? "")
    // 1. Ensure it's a string (if undefined/null → "")
    .toLowerCase()
    // 2. Use Unicode Normalization Form D (NFD):
    //    breaks accented characters into base + diacritic marks.
    //    e.g. "é" → "e" + "́"
    .normalize("NFD")
    // 3. Strip all combining diacritics using Unicode property escapes.
    //    Removes things like ́, ̈, etc.
    //    So "é" → "e", "ö" → "o".
    .replace(/\p{Diacritic}/gu, "")
    // 4. Replace different dash characters (en-dash, em-dash, minus sign) with a plain hyphen "-".
    .replace(/[–—-]/g, " ")
    // 5. Replace dots and commas with spaces.
    //    Makes tokens like "220i,AMG" → "220i AMG".
    .replace(/[.,]/g, " ")
    // 6. Collapse multiple whitespace into a single space.
    .replace(/\s+/g, " ")
    // 7. Trim leading/trailing whitespace.
    .trim();
}

export function extractTokens(str: string) {
  return normalize(str)?.split(" ")?.filter(Boolean)
}


/** Made by ChatGPT */
export function stableHash(value: unknown): string {
  return hashString(stableStringify(value));
}

/** Made by ChatGPT */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();

  return `{${keys.map(k => `${k}:${stableStringify(obj[k])}`).join(",")}}`;
}

/** Made by ChatGPT */
export function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

export function constructStringFromKeys<T extends Record<string, any>>(
  obj: T,
  keys: string[]
): string {
  let str = ""

  for (const keyPath of keys) {
    const value = keyPath.split(".").reduce<any>(
      (acc, part) => (acc && typeof acc === "object" ? acc[part] : undefined),
      obj
    )

    if (value != null && (typeof value === "string" || typeof value === "number")) {
      str += " " + value
    }
  }

  return str.trim()
}

